import React, { useState, useEffect } from 'react';
import { plansAPI, customersAPI } from '../../services/api';
import { formatCurrency } from '../../utils/helpers';
import Modal from '../../components/common/Modal';
import PermissionGuard from '../../components/common/PermissionGuard';
import Spinner from '../../components/common/Spinner';
import toast from 'react-hot-toast';

// ── Billing cycle accent colours ─────────────────────────────────────────────
const CYCLE_ACCENT = {
  monthly: { bar: 'bg-blue-500',   badge: 'bg-blue-100 text-blue-700',   ring: 'ring-blue-100'   },
  yearly:  { bar: 'bg-violet-500', badge: 'bg-violet-100 text-violet-700',ring: 'ring-violet-100' },
  weekly:  { bar: 'bg-emerald-500',badge: 'bg-emerald-100 text-emerald-700',ring:'ring-emerald-100'},
  trial:   { bar: 'bg-amber-500',  badge: 'bg-amber-100 text-amber-700',  ring: 'ring-amber-100'  },
};

// ── Single limit stat inside the card ────────────────────────────────────────
const LimitStat = ({ icon, label, value }) => (
  <div className="flex flex-col gap-0.5 bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
    <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide leading-none">{icon} {label}</span>
    <span className="text-sm font-bold text-gray-700 leading-tight">
      {value === -1 ? <span className="text-gray-400 font-normal">∞ Unlimited</span> : value.toLocaleString()}
    </span>
  </div>
);

// ── Limit input inside the form ───────────────────────────────────────────────
const LimitInput = ({ icon, label, hint, fieldKey, form, setForm, color }) => {
  const rawValue = form[fieldKey];
  const numValue = typeof rawValue === 'string' ? parseInt(rawValue) : rawValue;
  const displayValue = rawValue === '' || rawValue === '-' ? rawValue : numValue;
  return (
    <div className={`border border-${color}-200 rounded-xl p-4 bg-${color}-50/60`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-base">{icon}</span>
        <div>
          <h4 className={`font-semibold text-${color}-800 text-xs`}>{label}</h4>
          <p className="text-[10px] text-gray-400">{hint}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="number"
          min="-1"
          value={displayValue}
          onChange={e => {
            const raw = e.target.value;
            if (raw === '' || raw === '-') {
              setForm({ ...form, [fieldKey]: raw });
            } else {
              const v = parseInt(raw);
              if (!isNaN(v) && v >= -1) setForm({ ...form, [fieldKey]: v });
            }
          }}
          onBlur={() => {
            if (rawValue === '' || rawValue === '-') setForm({ ...form, [fieldKey]: 0 });
          }}
          className={`flex-1 px-3 py-1.5 border border-${color}-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-${color}-400`}
          placeholder="0"
        />
        <button
          type="button"
          onClick={() => setForm({ ...form, [fieldKey]: -1 })}
          className={`px-2.5 py-1.5 text-[11px] rounded-lg border border-${color}-200 ${numValue === -1 ? `bg-${color}-600 text-white` : `bg-white text-${color}-600 hover:bg-${color}-100`} transition font-medium`}
        >
          ∞ Unlimited
        </button>
        <button
          type="button"
          onClick={() => setForm({ ...form, [fieldKey]: 0 })}
          className={`px-2.5 py-1.5 text-[11px] rounded-lg border border-${color}-200 ${numValue === 0 ? `bg-${color}-600 text-white` : `bg-white text-${color}-600 hover:bg-${color}-100`} transition font-medium`}
        >
          0
        </button>
      </div>
      <p className={`text-[10px] mt-1.5 ${numValue === -1 ? `text-${color}-400 italic` : numValue === 0 ? `text-red-500` : `text-${color}-700`}`}>
        {numValue === -1 ? 'No limit — customers use freely.' : numValue === 0 ? 'Blocked — customers cannot use this feature.' : `Blocked after ${numValue.toLocaleString()} uses/cycle.`}
      </p>
    </div>
  );
};

const EMPTY_FORM = {
  name: '', description: '', price: '', billingCycle: 'monthly', trialDays: 0,
  aicustmoersLimit: -1, receiptScanLimit: -1, manualInvoiceLimit: -1, apiitemsLimit: -1,
  customersLimit: -1, itemsLimit: -1,
  features: [], isPopular: false
};

const LIMITS_CONFIG = [
  { icon: '👥', label: 'Customers',      fieldKey: 'customersLimit',    color: 'indigo' },
  { icon: '🧾', label: 'AI Invoice Scans',  fieldKey: 'receiptScanLimit',  color: 'blue'   },
  { icon: '📄', label: 'Manual Invoices',fieldKey: 'manualInvoiceLimit',color: 'green'  },
  { icon: '📦', label: 'Items',          fieldKey: 'itemsLimit',        color: 'orange' },
];

// ── Small icon-style action button ───────────────────────────────────────────
const ActionBtn = ({ onClick, className, children }) => (
  <button
    onClick={onClick}
    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${className}`}
  >
    {children}
  </button>
);

const Plans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editPlan, setEditPlan] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receiptPlan, setReceiptPlan] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [receiptForm, setReceiptForm] = useState({ customerId: '', amount: 0, vendor: '', notes: '' });
  const [savingReceipt, setSavingReceipt] = useState(false);

  const fetchPlans = async () => {
    setLoading(true);
    try { const res = await plansAPI.getAll(); setPlans(res.data.data); }
    catch { toast.error('Failed to load plans'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchPlans(); }, []);

  const openCreate = () => { setEditPlan(null); setForm(EMPTY_FORM); setShowModal(true); };
  const openEdit = (plan) => {
    setEditPlan(plan);
    setForm({
      name: plan.name, description: plan.description || '', price: plan.price,
      billingCycle: plan.billingCycle, trialDays: plan.trialDays || 0,
      aicustmoersLimit:   plan.aicustmoersLimit   ?? -1,
      receiptScanLimit:   plan.receiptScanLimit    ?? -1,
      manualInvoiceLimit: plan.manualInvoiceLimit  ?? -1,
      apiitemsLimit:      plan.apiitemsLimit       ?? -1,
      customersLimit:     plan.customersLimit      ?? -1,
      itemsLimit:         plan.itemsLimit          ?? -1,
      features: plan.features || [], isPopular: plan.isPopular || false
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editPlan) { await plansAPI.update(editPlan._id, form); toast.success('Plan updated'); }
      else          { await plansAPI.create(form);               toast.success('Plan created'); }
      setShowModal(false);
      fetchPlans();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this plan? This cannot be undone.')) return;
    try { await plansAPI.delete(id); toast.success('Plan deleted'); setPlans(plans.filter(p => p._id !== id)); }
    catch (err) { toast.error(err.response?.data?.message || 'Delete failed'); }
  };

  const handleToggle = async (id) => {
    try {
      const res = await plansAPI.toggleStatus(id);
      setPlans(plans.map(p => p._id === id ? res.data.data : p));
      toast.success('Status updated');
    } catch { toast.error('Failed'); }
  };

  const handleToggleVisibility = async (id) => {
    try {
      const res = await plansAPI.toggleVisibility(id);
      setPlans(plans.map(p => p._id === id ? res.data.data : p));
      toast.success('Visibility updated');
    } catch { toast.error('Failed to update visibility'); }
  };

  const openManualReceipt = async (plan) => {
    setReceiptPlan(plan);
    setReceiptForm({ customerId: '', amount: 0, vendor: plan.name, notes: '' });
    try {
      const res = await customersAPI.getAll({ limit: 200 });
      setCustomers(res.data.data);
    } catch { toast.error('Failed to load customers'); }
    setShowReceiptModal(true);
  };

  const handleReceiptSubmit = async (e) => {
    e.preventDefault();
    if (!receiptForm.customerId) return toast.error('Please select a customer');
    setSavingReceipt(true);
    try {
      await plansAPI.createManualReceipt(receiptPlan._id, receiptForm);
      toast.success('Manual receipt created');
      setShowReceiptModal(false);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to create receipt'); }
    finally { setSavingReceipt(false); }
  };

  const addFeature    = () => setForm({ ...form, features: [...form.features, { name: '', limit: -1, unit: '' }] });
  const removeFeature = (i) => setForm({ ...form, features: form.features.filter((_, idx) => idx !== i) });
  const updateFeature = (i, field, val) => {
    const f = [...form.features];
    f[i] = { ...f[i], [field]: val };
    setForm({ ...form, features: f });
  };

  if (loading) return <div className="flex justify-center items-center py-24"><Spinner size="lg" /></div>;

  const totalActive  = plans.filter(p => p.isActive).length;
  const totalHidden  = plans.filter(p => p.isVisible === false).length;

  return (
    <div className="space-y-6">

      {/* ── Page header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Plans</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {plans.length} total &nbsp;·&nbsp; {totalActive} active &nbsp;·&nbsp; {totalHidden} hidden
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-400 hidden sm:block">Customers are blocked when they exceed a limit</span>
          <PermissionGuard module="plans" action="create">
            <button
              onClick={openCreate}
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-indigo-700 active:scale-95 transition-all shadow-sm whitespace-nowrap"
            >
              + New Plan
            </button>
          </PermissionGuard>
        </div>
      </div>

      {/* ── Plan cards grid ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {plans.map(plan => {
          const accent = CYCLE_ACCENT[plan.billingCycle] || CYCLE_ACCENT.monthly;
          const isHidden = plan.isVisible === false;
          return (
            <div
              key={plan._id}
              className={`relative bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow flex flex-col overflow-hidden ${isHidden ? 'opacity-70' : ''}`}
            >
              {/* Coloured top accent bar */}
              <div className={`h-1.5 w-full ${accent.bar}`} />

              <div className="p-5 flex flex-col gap-4 flex-1">

                {/* Top row: name + badges */}
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold text-gray-900 text-base leading-tight truncate">{plan.name}</h3>
                      {plan.isPopular && (
                        <span className="bg-indigo-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide">
                          Popular
                        </span>
                      )}
                    </div>
                    {plan.description && (
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2">{plan.description}</p>
                    )}
                  </div>
                </div>

                {/* Price */}
                <div className="flex items-end gap-1.5">
                  <span className="text-3xl font-extrabold text-gray-900 leading-none">{formatCurrency(plan.price)}</span>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize mb-0.5 ${accent.badge}`}>
                    {plan.billingCycle}
                  </span>
                </div>
                {plan.trialDays > 0 && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 -mt-1">
                    🎁 {plan.trialDays}-day free trial included
                  </p>
                )}

                {/* Usage limits */}
                <div className="grid grid-cols-2 gap-1.5">
                  <LimitStat icon="👥" label="Customers"    value={plan.customersLimit     ?? -1} />
                  <LimitStat icon="🧾" label="AI Invoice Scans" value={plan.receiptScanLimit   ?? -1} />
                  <LimitStat icon="📄" label="Invoices"     value={plan.manualInvoiceLimit ?? -1} />
                  <LimitStat icon="📦" label="Items"        value={plan.itemsLimit         ?? -1} />
                </div>

                {/* Extra features */}
                {plan.features?.length > 0 && (
                  <ul className="space-y-1 border-t border-gray-100 pt-2">
                    {plan.features.map((f, i) => (
                      <li key={i} className="text-xs text-gray-600 flex items-center gap-1.5">
                        <span className="text-green-500 font-bold">✓</span>
                        {f.name}{f.limit !== -1 ? `: ${f.limit} ${f.unit}` : ''}
                      </li>
                    ))}
                  </ul>
                )}

                {/* Footer: status badges + actions */}
                <div className="mt-auto pt-3 border-t border-gray-100 flex flex-col gap-2">
                  {/* Status badges */}
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${plan.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${plan.isActive ? 'bg-green-500' : 'bg-gray-400'}`} />
                      {plan.isActive ? 'Active' : 'Inactive'}
                    </span>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${isHidden ? 'bg-red-100 text-red-600' : 'bg-sky-100 text-sky-700'}`}>
                      {isHidden ? '🚫 Hidden from customers' : '👁 Visible to customers'}
                    </span>
                  </div>

                  {/* Action buttons */}
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <PermissionGuard module="plans" action="create">
                      {/* <ActionBtn onClick={() => openManualReceipt(plan)} className="bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200">
                        🧾 Receipt
                      </ActionBtn> */}
                    </PermissionGuard>
                    <PermissionGuard module="plans" action="update">
                      <ActionBtn onClick={() => openEdit(plan)} className="bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200">
                        ✏️ Edit
                      </ActionBtn>
                      <ActionBtn onClick={() => handleToggle(plan._id)} className={plan.isActive ? 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100 border border-yellow-200' : 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'}>
                        {plan.isActive ? '⏸ Disable' : '▶ Enable'}
                      </ActionBtn>
                      <ActionBtn onClick={() => handleToggleVisibility(plan._id)} className={isHidden ? 'bg-sky-50 text-sky-700 hover:bg-sky-100 border border-sky-200' : 'bg-slate-50 text-slate-600 hover:bg-slate-100 border border-slate-200'}>
                        {isHidden ? '👁 Show' : '🙈 Hide'}
                      </ActionBtn>
                    </PermissionGuard>
                    <PermissionGuard module="plans" action="delete">
                      <ActionBtn onClick={() => handleDelete(plan._id)} className="bg-red-50 text-red-600 hover:bg-red-100 border border-red-200">
                        🗑 Delete
                      </ActionBtn>
                    </PermissionGuard>
                  </div>
                </div>

              </div>
            </div>
          );
        })}

        {/* Empty state */}
        {plans.length === 0 && (
          <div className="col-span-3 flex flex-col items-center justify-center py-24 text-center">
            <div className="w-16 h-16 bg-indigo-50 rounded-2xl flex items-center justify-center text-3xl mb-4">📦</div>
            <h3 className="font-semibold text-gray-700 text-base">No plans yet</h3>
            <p className="text-sm text-gray-400 mt-1 mb-5">Create your first plan to start onboarding customers.</p>
            <PermissionGuard module="plans" action="create">
              <button onClick={openCreate} className="bg-indigo-600 text-white px-5 py-2 rounded-lg text-sm font-semibold hover:bg-indigo-700 transition shadow-sm">
                + Create First Plan
              </button>
            </PermissionGuard>
          </div>
        )}
      </div>

      {/* ── Create / Edit Modal ── */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editPlan ? 'Edit Plan' : 'Create New Plan'} size="xl">
        <form onSubmit={handleSubmit} className="space-y-5">

          {/* Basic info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 sm:col-span-1">
              <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">Plan Name <span className="text-red-500">*</span></label>
              <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required placeholder="e.g. Professional"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">Price (MYR) <span className="text-red-500">*</span></label>
              <input type="number" min="0" step="0.01" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} required placeholder="0.00"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">Billing Cycle <span className="text-red-500">*</span></label>
              <select value={form.billingCycle} onChange={e => setForm({ ...form, billingCycle: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                {['weekly', 'monthly', 'yearly', 'trial'].map(c => (
                  <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">Trial Days</label>
              <input type="number" min="0" value={form.trialDays} onChange={e => setForm({ ...form, trialDays: parseInt(e.target.value) || 0 })} placeholder="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">Description</label>
            <input type="text" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Short plan summary shown to customers"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </div>

          <label className="flex items-center gap-2 cursor-pointer w-fit">
            <input type="checkbox" id="isPopular" checked={form.isPopular} onChange={e => setForm({ ...form, isPopular: e.target.checked })}
              className="w-4 h-4 text-indigo-600 rounded border-gray-300" />
            <span className="text-sm text-gray-700 font-medium">Mark as Popular</span>
            <span className="text-xs text-gray-400">(shows a "Popular" badge)</span>
          </label>

          {/* Usage limits */}
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-[11px] font-bold text-gray-400 uppercase tracking-widest whitespace-nowrap">Usage Limits</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>
            <p className="text-xs text-gray-400 mb-3">Set to –1 for unlimited. Customers are <strong className="text-gray-600">auto-blocked</strong> when any limit is exceeded.</p>
            <div className="grid grid-cols-2 gap-3">
              {LIMITS_CONFIG.map(cfg => (
                <LimitInput key={cfg.fieldKey} {...cfg} hint="per billing cycle" form={form} setForm={setForm} />
              ))}
            </div>
          </div>

          {/* Additional features */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Additional Features</label>
              <button type="button" onClick={addFeature} className="text-indigo-600 text-xs font-semibold hover:text-indigo-800 transition">+ Add feature</button>
            </div>
            {form.features.length === 0 && (
              <p className="text-xs text-gray-400 italic">No additional features. Click "+ Add feature" to add one.</p>
            )}
            <div className="space-y-2">
              {form.features.map((f, i) => (
                <div key={i} className="flex gap-2 items-center bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                  <input type="text" placeholder="Feature name" value={f.name} onChange={e => updateFeature(i, 'name', e.target.value)}
                    className="flex-1 bg-transparent text-sm focus:outline-none text-gray-700 placeholder-gray-400" />
                  <input type="number" placeholder="Limit" value={f.limit} onChange={e => updateFeature(i, 'limit', parseInt(e.target.value))}
                    className="w-16 bg-transparent text-sm text-center focus:outline-none text-gray-700 border-l border-gray-200 pl-2" />
                  <input type="text" placeholder="Unit" value={f.unit} onChange={e => updateFeature(i, 'unit', e.target.value)}
                    className="w-16 bg-transparent text-sm focus:outline-none text-gray-700 border-l border-gray-200 pl-2" />
                  <button type="button" onClick={() => removeFeature(i)} className="text-gray-400 hover:text-red-500 transition ml-1 font-bold text-base leading-none">×</button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-3 border-t border-gray-100">
            <button type="button" onClick={() => setShowModal(false)}
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 bg-indigo-600 text-white px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 transition shadow-sm">
              {saving ? 'Saving…' : (editPlan ? 'Update Plan' : 'Create Plan')}
            </button>
          </div>
        </form>
      </Modal>

      {/* ── Manual Receipt Modal ── */}
      <Modal isOpen={showReceiptModal} onClose={() => setShowReceiptModal(false)} title="Create Manual Receipt" size="md">
        <form onSubmit={handleReceiptSubmit} className="space-y-4">

          {/* Plan info banner */}
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-3 flex items-center gap-3">
            <span className="text-xl">📋</span>
            <div>
              <p className="text-xs font-semibold text-indigo-800">{receiptPlan?.name}</p>
              <p className="text-[11px] text-indigo-500">Manual receipt — set amount to 0 for activation-code users</p>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">Customer <span className="text-red-500">*</span></label>
            <select value={receiptForm.customerId} onChange={e => setReceiptForm({ ...receiptForm, customerId: e.target.value })} required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white">
              <option value="">— Select customer —</option>
              {customers.map(c => (
                <option key={c._id} value={c._id}>{c.name} ({c.email})</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">Amount (MYR)</label>
            <input type="number" min="0" step="0.01" value={receiptForm.amount}
              onChange={e => setReceiptForm({ ...receiptForm, amount: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            {parseFloat(receiptForm.amount) === 0 && (
              <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                <span>✓</span> Zero-amount — no payment charged
              </p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">Vendor / Description</label>
            <input type="text" value={receiptForm.vendor} onChange={e => setReceiptForm({ ...receiptForm, vendor: e.target.value })}
              placeholder={receiptPlan?.name}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">Notes</label>
            <input type="text" value={receiptForm.notes} onChange={e => setReceiptForm({ ...receiptForm, notes: e.target.value })}
              placeholder="e.g. Activated via reseller code"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </div>

          <div className="flex gap-3 pt-3 border-t border-gray-100">
            <button type="button" onClick={() => setShowReceiptModal(false)}
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition">
              Cancel
            </button>
            <button type="submit" disabled={savingReceipt}
              className="flex-1 bg-emerald-600 text-white px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60 transition shadow-sm">
              {savingReceipt ? 'Creating…' : 'Create Receipt'}
            </button>
          </div>
        </form>
      </Modal>

    </div>
  );
};

export default Plans;
